/**
 * Status utilities for consistent task status display across the application
 */

export type TaskStatusType = "to_do" | "in_progress" | "incomplete" | "complete" | string;

/**
 * Normalize status values to display names
 */
export const normalizeTaskStatus = (status?: string): string => {
  if (!status) return "To Do";

  const normalized = status.toLowerCase().trim();

  const statusMap: Record<string, string> = {
    to_do: "To Do",
    in_progress: "In Progress",
    incomplete: "Incomplete",
    complete: "Complete",
  };

  return statusMap[normalized] || status;
};

/**
 * Get the primary color for a task status
 */
export const getTaskStatusColor = (status?: string): string => {
  const normalized = status?.toLowerCase().trim() || "to_do";

  switch (normalized) {
    case "to_do":
      return "#8A8886"; // Gray
    case "in_progress":
      return "#0078D4"; // Blue
    case "incomplete":
      return "#D73B02"; // Red
    case "complete":
      return "#107C10"; // Green
    default:
      return "#8A8886"; // Default gray
  }
};

/**
 * Get a lighter version of the status color (for backgrounds)
 */
export const getTaskStatusColorLight = (status?: string): string => {
  const normalized = status?.toLowerCase().trim() || "to_do";

  switch (normalized) {
    case "to_do":
      return "#F3F2F1"; // Light gray
    case "in_progress":
      return "#D6EBFF"; // Light blue
    case "incomplete":
      return "#FDE7E9"; // Light red
    case "complete":
      return "#DFF6DD"; // Light green
    default:
      return "#F3F2F1"; // Default light gray
  }
};

/**
 * Get a darker version of the status color (for text/borders)
 */
export const getTaskStatusColorDark = (status?: string): string => {
  const normalized = status?.toLowerCase().trim() || "to_do";

  switch (normalized) {
    case "to_do":
      return "#605E5C"; // Dark gray
    case "in_progress":
      return "#005A9E"; // Dark blue
    case "incomplete":
      return "#A72E00"; // Dark red
    case "complete":
      return "#0B5A08"; // Dark green
    default:
      return "#605E5C"; // Default dark gray
  }
};
